/**
 * Social Share Utilities
 * Generates share URLs with Open Graph meta tag support for social media previews
 */

// API Gateway endpoint for share preview handler
const SHARE_API_BASE_URL = process.env.REACT_APP_SHARE_API_URL ||
  'https://share.hamekomit.com';

/**
 * Get the share URL for a business, event, or store
 * This URL will generate social media previews (WhatsApp, Telegram, Facebook)
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @returns {string} The share URL that generates OG previews
 */
export function getShareUrl(type, id) {
  if (!type || !id) {
    console.error('getShareUrl: type and id are required');
    return '';
  }

  // Validate type
  if (type !== 'business' && type !== 'event' && type !== 'store') {
    console.error('getShareUrl: type must be "business", "event", or "store"');
    return '';
  }

  // Return the share preview endpoint URL
  return `${SHARE_API_BASE_URL}/share/${type}/${id}`;
}

/**
 * Get WhatsApp share link
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @param {string} name - The business, event, or store name
 * @param {string} customMessage - Optional custom message (default: "בואו לראות את {name} במקומית!")
 * @returns {string} WhatsApp share URL
 */
export function getWhatsAppShareLink(type, id, name, customMessage = null) {
  const shareUrl = getShareUrl(type, id);
  const message = customMessage || `בואו לראות את ${name} במקומית!`;
  const fullText = `${message} ${shareUrl}`;

  return `https://wa.me/?text=${encodeURIComponent(fullText)}`;
}

/**
 * Get Telegram share link
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @param {string} name - The business, event, or store name
 * @param {string} customMessage - Optional custom message (default: "בואו לראות את {name} במקומית!")
 * @returns {string} Telegram share URL
 */
export function getTelegramShareLink(type, id, name, customMessage = null) {
  const shareUrl = getShareUrl(type, id);
  const message = customMessage || `בואו לראות את ${name} במקומית!`;

  return `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`;
}

/**
 * Get Facebook share link
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @returns {string} Facebook share URL
 */
export function getFacebookShareLink(type, id) {
  const shareUrl = getShareUrl(type, id);
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
}

/**
 * Share using the native Web Share API if available, with fallback
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @param {string} name - The business, event, or store name
 * @param {string} description - Optional description
 * @returns {Promise<boolean>} True if shared successfully, false otherwise
 */
export async function shareNative(type, id, name, description = '') {
  const shareUrl = getShareUrl(type, id);

  if (navigator.share) {
    try {
      await navigator.share({
        title: name,
        text: description || `בואו לראות את ${name} במקומית!`,
        url: shareUrl,
      });
      return true;
    } catch (error) {
      // User cancelled or error occurred
      console.log('Share cancelled or failed:', error);
      return false;
    }
  } else {
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }
}

/**
 * Copy share URL to clipboard
 *
 * @param {string} type - Either 'business', 'event', or 'store'
 * @param {string} id - The business, event, or store ID (for stores, use slug)
 * @returns {Promise<boolean>} True if copied successfully
 */
export async function copyShareUrl(type, id) {
  const shareUrl = getShareUrl(type, id);

  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    }
  } catch (error) {
    console.error('Failed to copy share URL:', error);
    return false;
  }
}
